var _numeralFormat;

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import * as R from 'ramda';
import dateFns from 'date-fns';
import numeral from 'numeral';
import { YEAR, SEMESTER, QUARTER, MONTH, WEEK, DAY, HOUR, FULL_HOUR, MINUTE, YEARS, SEMESTERS, QUARTERS, MONTHS, WEEKS, DAYS, FULL_HOURS, HOURS, MINUTES, layoutFrequencies, frequencies, defaultOutputValues, formatOrder, defaultfreq } from './constants';

var isWeekInPreviousYear = function isWeekInPreviousYear(date) {
  var startIsoWeek = dateFns.startOfISOWeek(date);
  return R.pipe(R.times(function (n) {
    return dateFns.getYear(dateFns.addDays(startIsoWeek, n));
  }), R.uniq, R.length, R.equals(1))(4);
};
var supportedFrequenciesKey = R.keys(layoutFrequencies);
var dec = R.ifElse(R.gte(0), R.identity, R.dec);
var addSemesters = function addSemesters() {
  var semester = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  return function (date) {
    return dateFns.addMonths(date, R.multiply(6)(dec(Number(semester))));
  };
};
var getSemesterValue = R.ifElse(R.pipe(Number, R.inc, R.gte(6)), R.always(1), R.always(2));
var getFormat = function getFormat(value) {
  return numeral(value).format('[0]0');
};
var getHour = R.pipe(R.pick([HOUR, FULL_HOUR]), R.values, R.head);
var getRanges = function getRanges() {
  var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [0];
  return R.range(R.head(value), R.inc(R.last(value)));
};
var setRange = function setRange(range) {
  return function (prop) {
    return R.pipe(getRanges, R.prepend(R.prop(prop)(defaultOutputValues)))(R.prop(prop)(range));
  };
};

export var getDestructuringDate = function getDestructuringDate(date) {
  return R.pipe(function (date) {
    return dateFns.format(date, 'YYYY M D H m H Q W [S]');
  }, R.replace(/S/, R.pipe(function (date) {
    return dateFns.getMonth(date);
  }, getSemesterValue)(date)), R.split(' '), R.ifElse(function (v) {
    return R.equals(R.length(formatOrder), R.length(v));
  }, R.map(Number), R.empty))(date);
};

var updateLimit = function updateLimit(value, isSamePeriod, defaultValue) {
  if (isSamePeriod) return value;
  return defaultValue;
};

export var getRange = R.curry(function (boundaries, date) {
  var _ref3;

  var destructuredDate = getDestructuringDate(date);
  var destructuredBoundaries = R.map(getDestructuringDate, boundaries);

  var _R$transpose = R.transpose(destructuredBoundaries),
      years = _R$transpose[0],
      months = _R$transpose[1],
      days = _R$transpose[2],
      hours = _R$transpose[3],
      minutes = _R$transpose[4],
      quarters = _R$transpose[6],
      weeks = _R$transpose[7],
      semesters = _R$transpose[8];

  if (R.isNil(date)) {
    var _ref;

    return _ref = {}, _ref[YEARS] = years, _ref[HOURS] = [NaN], _ref[FULL_HOURS] = [NaN], _ref[MONTHS] = [NaN], _ref[WEEKS] = [NaN], _ref[SEMESTERS] = [NaN], _ref[QUARTERS] = [NaN], _ref[DAYS] = [NaN], _ref[MINUTES] = [NaN], _ref;
  }
  var listEqualsValueFromDate = R.pipe(R.transpose, R.reduce(function (acc, _ref2) {
    var _extends2;

    var type = _ref2[0],
        date = _ref2[1],
        start = _ref2[2],
        end = _ref2[3];

    return _extends({}, acc, (_extends2 = {}, _extends2[type] = [R.equals(date, start), R.equals(date, end)], _extends2));
  }, {}))([frequencies, destructuredDate].concat(destructuredBoundaries));

  var equalsYears = R.prop('years')(listEqualsValueFromDate);
  var equalsMonths = R.prop('months')(listEqualsValueFromDate);
  var equalsDays = R.prop('days')(listEqualsValueFromDate);

  var hoursRange = R.addIndex(R.map)(function (value, index) {
    var isSamePeriod = R.all(R.identity)([R.nth(index, equalsDays), R.nth(index, equalsMonths), R.nth(index, equalsYears)]);
    return updateLimit(R.nth(index)(hours), isSamePeriod, value);
  })([0, 23]);

  return _ref3 = {}, _ref3[YEARS] = years, _ref3[HOURS] = hoursRange, _ref3[FULL_HOURS] = hoursRange, _ref3[MONTHS] = R.addIndex(R.map)(function (value, index) {
    return updateLimit(R.nth(index)(months), R.nth(index, equalsYears), value);
  })([1, 12]), _ref3[WEEKS] = R.addIndex(R.map)(function (value, index) {
    return updateLimit(R.nth(index)(weeks), R.nth(index, equalsYears), value);
  })([1, dateFns.getISOWeeksInYear(date)]), _ref3[SEMESTERS] = R.addIndex(R.map)(function (value, index) {
    return updateLimit(R.nth(index)(semesters), R.nth(index, equalsYears), value);
  })([1, 2]), _ref3[QUARTERS] = R.addIndex(R.map)(function (value, index) {
    return updateLimit(R.nth(index)(quarters), R.nth(index, equalsYears), value);
  })([1, 4]), _ref3[DAYS] = R.addIndex(R.map)(function (value, index) {
    var isSamePeriod = R.and(R.nth(index, equalsMonths), R.nth(index, equalsYears));
    return updateLimit(R.nth(index)(days), isSamePeriod, value);
  })([1, dateFns.getDaysInMonth(date)]), _ref3[MINUTES] = R.addIndex(R.map)(function (value, index) {
    var isSamePeriod = R.all(R.identity)([R.nth(index, R.prop('hours')(listEqualsValueFromDate)), R.nth(index, equalsDays), R.nth(index, equalsMonths), R.nth(index, equalsYears)]);
    return updateLimit(R.nth(index)(minutes), isSamePeriod, value);
  })([0, 59]), _ref3;
});

export var getAjustedDate = function getAjustedDate(frequency) {
  return R.when(R.always(R.or(R.equals('W', frequency), R.equals('B', frequency))), R.ifElse(isWeekInPreviousYear, dateFns.startOfISOWeek, dateFns.endOfISOWeek));
};

export var isValidNumber = R.both(R.is(Number), R.complement(R.equals(NaN)));
export var getDefaultFrequency = function getDefaultFrequency(_ref4) {
  var defaultFrequency = _ref4.defaultFrequency,
      frequencies = _ref4.frequencies;

  var isNotIncludes = R.complement(R.and)(R.includes(defaultFrequency)(frequencies), R.includes(defaultFrequency)(supportedFrequenciesKey));
  return R.or(R.either(R.isEmpty, R.isNil)(defaultFrequency), isNotIncludes) ? R.head(frequencies) : defaultFrequency;
};

export var getFrequencyOptions = function getFrequencyOptions(frequencyoptions, frequencyLabels) {
  var availableFrequencies = R.or(R.isEmpty(frequencyoptions), R.isNil(frequencyoptions)) ? [defaultfreq] : R.filter(function (freq) {
    return R.includes(freq, supportedFrequenciesKey);
  })(frequencyoptions);
  var options = R.map(function (option) {
    return { value: option, label: R.prop(option, frequencyLabels) };
  })(availableFrequencies);
  if (R.isEmpty(options)) return [{ value: 'A', label: R.propOr('Annual', 'A', frequencyLabels) }];
  return options;
};

export var getLayout = function getLayout(type, range) {
  var layoutPeriods = R.propOr([], type)(layoutFrequencies);
  var hasYear = R.complement(R.isEmpty)(getRanges(range.years));
  return R.map(R.reduce(function (acc, period) {
    if (hasYear) return R.append(R.evolve({ values: setRange(range) }, period), acc);
    return R.append(R.evolve({ values: R.pipe(R.flip(R.prop)(defaultOutputValues), R.flip(R.append)([])) }, period), acc);
  }, []))(layoutPeriods);
};

export var getPeriodValue = R.pipe(R.zip(formatOrder), R.fromPairs);

export var getDate = function getDate(type, dateValue, value, isEnd) {
  if (R.isNil(value)) return undefined;
  var dateValueUpdated = R.pipe(R.ifElse(R.pipe(getHour, R.isNil), R.identity, R.set(R.lensProp('hour'), getHour(value))), R.mergeRight(dateValue), R.over(R.lensProp('month'), R.defaultTo(isEnd ? 12 : 1)), R.over(R.lensProp('day'), R.defaultTo(isEnd ? 31 : 1)), R.over(R.lensProp('hour'), R.defaultTo(isEnd ? 23 : 0)), R.over(R.lensProp('minute'), R.defaultTo(isEnd ? 59 : 0)))(value);
  var year = dateValueUpdated.year,
      month = dateValueUpdated.month,
      day = dateValueUpdated.day,
      hour = dateValueUpdated.hour,
      minute = dateValueUpdated.minute;

  var date = new Date(year, R.dec(month), day, hour, minute);
  if (R.equals(type, 'Q')) {
    if (R.isEmpty(dateValue) && isEnd) {
      return R.pipe(dateFns.startOfYear, R.flip(dateFns.addQuarters)(3))(date);
    }
    return R.pipe(dateFns.startOfYear, function (date) {
      return dateFns.addQuarters(date, dec(R.propOr(0, QUARTER)(dateValueUpdated)));
    })(date);
  }
  if (R.equals(type, 'S')) {
    if (R.isEmpty(dateValue) && isEnd) return R.pipe(dateFns.startOfYear, addSemesters(2))(date);
    return R.pipe(dateFns.startOfYear, addSemesters(R.prop(SEMESTER)(dateValueUpdated)))(date);
  }
  if (R.or(R.equals(type, 'W'), R.equals(type, 'B'))) {
    var weekDate = new Date(year, 6, 1, 0, 0, 0, 0);
    if (R.isEmpty(dateValue)) {
      return R.pipe(R.ifElse(R.always(isEnd), dateFns.endOfISOYear, dateFns.startOfISOYear), getAjustedDate(type))(weekDate);
    }
    var weeksInYear = R.dec(dateFns.getISOWeeksInYear(weekDate));
    var nbWeeks = R.pipe(R.propOr(0, WEEK), R.dec, R.ifElse(R.lt(weeksInYear), R.always(weeksInYear), R.identity))(dateValueUpdated);
    return R.pipe(dateFns.startOfISOYear, function (date) {
      return dateFns.addWeeks(date, nbWeeks);
    }, R.of, R.map(getAjustedDate(type)), R.head)(weekDate);
  }
  return date;
};

export var getDateInTheRange = function getDateInTheRange(boundaries) {
  return function (date) {
    if (R.isNil(date)) return date;
    return dateFns.isWithinRange.apply(dateFns, [date].concat(boundaries)) ? date : dateFns.closestTo(date, boundaries);
  };
};

export var numeralFormat = (_numeralFormat = {}, _numeralFormat[YEAR] = R.identity, _numeralFormat[SEMESTER] = R.identity, _numeralFormat[QUARTER] = R.identity, _numeralFormat[MONTH] = getFormat, _numeralFormat[WEEK] = getFormat, _numeralFormat[DAY] = getFormat, _numeralFormat[FULL_HOUR] = R.converge(R.concat, [getFormat, R.always(':00')]), _numeralFormat[HOUR] = getFormat, _numeralFormat[MINUTE] = getFormat, _numeralFormat);